import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { groupBy } from "lodash";
import { useFragment } from "react-relay";

import { useGetDatesListData_staffProfile$key } from "./__generated__/useGetDatesListData_staffProfile.graphql";

type AvailableDateCard = {
  id: string;
  date: string;
  dateString: string;
  endDate?: string;
  endDateString?: string;
  instructorId: string;
  slotsCount?: number;
  durationMins?: number;
  lessonTypeId?: string;
};

export type AvailableDate = {
  id: string;
  date: string;
};

type Props = {
  dateRange: Date[];
  durationMins: number;
  lessonTypeId?: string;
  instructorId: string;
  instructorRef: useGetDatesListData_staffProfile$key | null;
  prevAvailableDates: AvailableDate[];
};

export const useGetDatesListData = ({
  dateRange,
  durationMins,
  lessonTypeId,
  instructorId,
  instructorRef,
  prevAvailableDates,
}: Props) => {
  const instructor = useFragment(
    useGetDatesListDataFragmentString,
    instructorRef
  );

  const newAvailableDates =
    instructor?.availabilitySlots?.edges?.flatMap((e) =>
      e?.node ? [e.node] : []
    ) ?? [];

  const availableDates = [...prevAvailableDates, ...newAvailableDates];

  const hasAvailability = availableDates.length > 0;

  const groupedByDate = groupBy(availableDates, "date");

  const dateListCards = reduceDatesToCards(
    dateRange,
    groupedByDate,
    durationMins,
    instructorId,
    lessonTypeId
  ).slice(0, 7);

  return { hasAvailability, dateList: dateListCards, availableDates } as const;
};
const useGetDatesListDataFragmentString = graphql`
  fragment useGetDatesListData_staffProfile on StaffProfile {
    availabilitySlots(
      dateTimeLte: $endDateTime
      dateTimeGte: $startDateTime
      durationMins: $durationMins
      timeGte: $startTime
      timeLte: $endTime
      slotAvailability: $slotAvailability
    ) {
      edges {
        node {
          id
          date
        }
      }
    }
  }
`;

const reduceDatesToCards = (
  dateRange: Date[],
  groupedByDate: Record<string, any[]>,
  durationMins: number,
  instructorId: string,
  lessonTypeId?: string
): AvailableDateCard[] => {
  return dateRange.reduce<AvailableDateCard[]>((acc, date) => {
    const dateString = format(date, "yyyy-MM-dd");
    const slots = groupedByDate[dateString];

    if (slots) {
      acc.push({
        id: dateString,
        dateString,
        date: format(date, "LLL do"),
        slotsCount: slots.length,
        lessonTypeId,
        durationMins,
        instructorId,
      });
    } else {
      const lastEntry = acc[acc.length - 1];
      if (lastEntry && !lastEntry.slotsCount) {
        lastEntry.endDate = format(date, "LLL do");
        lastEntry.endDateString = dateString;
      } else {
        acc.push({
          id: dateString,
          dateString,
          date: !lastEntry ? format(date, "LLL do") : format(date, "LLL do"),
          endDate: !lastEntry ? undefined : format(date, "LLL do"),
          endDateString: !lastEntry ? undefined : dateString,
          lessonTypeId,
          durationMins,
          instructorId,
        });
      }
    }

    return acc;
  }, []);
};
